import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { CustomerDialog } from "@/components/CustomerDialog";
import { CustomerImportButton } from "@/components/CustomerImport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Customers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => api.get("/customers", { params: { q } }).then((r) => setRows(r.data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Customer deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle={`${rows.length} customers`}>
        {user.role === "admin" && <CustomerImportButton onDone={load} />}
        <Button className="bg-[#1BA3E0] hover:bg-[#1590C8]" onClick={() => { setEdit(null); setOpen(true); }} data-testid="new-customer-btn"><Plus className="mr-1 h-4 w-4" /> New customer</Button>
      </PageHeader>
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input placeholder="Search name, contact, email, ABN" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="customers-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Business</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Email / Phone</th><th className="px-4 py-3">ABN</th><th className="px-4 py-3">Terms</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="row-hover border-t border-slate-100" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-[#0B1523]">{c.name}</td>
                  <td className="px-4 py-3">{c.contact_name}</td>
                  <td className="px-4 py-3 text-slate-500"><div>{c.email}</div><div className="text-xs">{c.phone}</div></td>
                  <td className="num px-4 py-3 text-xs">{c.abn}</td>
                  <td className="px-4 py-3 text-slate-500">{c.payment_terms}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setEdit(c); setOpen(true); }} data-testid={`customer-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    {user.role === "admin" && <Button size="icon" variant="ghost" onClick={() => del(c)} data-testid={`customer-delete-${c.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-500" data-testid="customers-empty">No customers found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <CustomerDialog open={open} onOpenChange={setOpen} customer={edit} onSaved={load} />
    </div>
  );
}
